import { useEffect, useState } from 'react';

import { Permissions } from 'constants/permissions';
import { useAccessControl } from 'hooks/access-control';
import { useTracking } from 'hooks/analytic-tracking';
import { useDocumentTitle } from 'hooks/document-title';
import AppPage from 'layouts/AppPage/AppPage';
import { Tab, Tabs } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { messagePopUp } from 'api/app-slice';
import { selectCompany } from 'api/company-slice';
import { selectPermissions } from 'api/user-permissions-slice';
import { selectUser } from 'api/user-slice';

import { AuditLogs } from './tabs/AuditLogs/AuditLogs';
import { AuthenticationSecurity } from './tabs/AuthenticationSecurity/AuthenticationSecurity';
import { Billing } from './tabs/Billing/Billing';
import { Branding } from './tabs/Branding/Branding';
import { Bundles } from './tabs/Bundles/Bundles';
import { CustomFields } from './tabs/CustomFields/CustomFields';
import { DataProtection } from './tabs/DataProtection/DataProtection';
import { Integrations } from './tabs/Integrations/Integrations';
import { Settings } from './tabs/Settings/Settings';
import { Tags } from './tabs/Tags/Tags';

const Company = () => {
  useDocumentTitle('Company | Checkmate');

  const { tab: targetTab } = useParams();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const company = useSelector(selectCompany);
  const user = useSelector(selectUser);
  const permissions = useSelector(selectPermissions);

  const onMessagePopUp = (text, state, hide = true) =>
    dispatch(messagePopUp({ text, state, hide }));

  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(targetTab);

  const hasAccess = useAccessControl(permissions);

  useEffect(() => setTab(targetTab), [targetTab]);

  return (
    <AppPage loading={loading}>
      <AppPage.Header
        title="Company"
        subtitle="Below you can view and manage your company settings, users and roles"
      />
      <Tabs
        defaultActiveKey="settings"
        onSelect={key => navigate(`/company/${key}`)}
        activeKey={tab}
        mountOnEnter={true}
        className="tabs">
        <Tab eventKey="settings" title="Settings">
          <Settings company={company} setLoading={setLoading} />
        </Tab>
        <Tab eventKey="authentication_security" title="Authentication Security">
          <AuthenticationSecurity company={company} setLoading={setLoading} />
        </Tab>
        {hasAccess(Permissions.BillingWrite) && (
          <Tab eventKey="billing" title="Billing">
            <Billing setLoading={setLoading} />
          </Tab>
        )}
        {hasAccess(Permissions.BrandWrite) && (
          <Tab eventKey="branding" title="Branding">
            <Branding user={user} setLoading={setLoading} messagePopUp={onMessagePopUp} />
          </Tab>
        )}
        {hasAccess(Permissions.CheckBundlesWrite) && (
          <Tab eventKey="bundles" title="Bundles">
            <Bundles setLoading={setLoading} />
          </Tab>
        )}
        <Tab eventKey="data_protection" title="Data Protection">
          <DataProtection company={company} setLoading={setLoading} />
        </Tab>
        {company.audit_logs_enabled && (
          <Tab eventKey="audit_logs" title="Audit Logs">
            <AuditLogs company={company} setLoading={setLoading} />
          </Tab>
        )}
        <Tab eventKey="tags" title="Tags">
          <Tags setLoading={setLoading} />
        </Tab>
        <Tab eventKey="custom_fields" title="Custom Fields">
          <CustomFields setLoading={setLoading} />
        </Tab>
        {hasAccess(Permissions.PartnersWrite) && (
          <Tab eventKey="integrations" title="Integrations">
            <Integrations
              user={user}
              company={company}
              setLoading={setLoading}
              popUpMessage={onMessagePopUp}
            />
          </Tab>
        )}
      </Tabs>
    </AppPage>
  );
};

export default Company;
